import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createRequest, generateConcept, getIntakePrompts, searchTrainingClients } from './api';

const PERSONAS = [
  // "Client (relayed)" is an intake VOICE, not a buyer link. Picking it does not
  // attach a client; use the optional client field below to attach a real one.
  { id: 'client_casual', label: 'Client (relayed)' },
  { id: 'trainer', label: 'Trainer' },
  { id: 'internal_director', label: 'Director / internal' },
];

// Persona-specific prompt sets (see spec §10).
const STEPS = {
  client_casual: [
    { key: 'audience', q: 'Who needs the training?', help: 'Roles or teams.' },
    { key: 'program_type', q: 'What program / state is involved?', help: 'e.g. LIHTC · Ohio' },
    { key: 'need', q: 'What problem are you trying to solve?', help: 'Describe it in your own words.' },
    { key: 'learner_count', q: 'How many learners may need access?', help: 'A rough number is fine.', type: 'number' },
    { key: 'urgency', q: 'Is this urgent or exploratory?', help: 'Any deadline, audit, or finding?' },
  ],
  trainer: [
    { key: 'need', q: 'What did learners struggle with?', help: 'The specific gap.' },
    { key: 'audience', q: 'What course / class / topic is affected?', help: '' },
    { key: 'urgency', q: 'Is the issue urgent or recurring?', help: '' },
  ],
  internal_director: [
    { key: 'need', q: 'What is the course idea or training need?', help: '' },
    { key: 'audience', q: 'Who is the learner and what must improve?', help: '' },
    { key: 'program_type', q: 'Program / state?', help: '' },
  ],
};

const BUYER_STAGES = [
  ['exploring', 'Exploring'],
  ['shopping', 'Shopping'],
  ['internal_approval', 'Internal approval'],
  ['ready_to_schedule', 'Ready to schedule'],
  ['ready_to_buy', 'Ready to buy'],
];

export default function GuidedIntake({ onGenerated, onCancel }) {
  const [persona, setPersona] = useState('client_casual');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [buyerStage, setBuyerStage] = useState('shopping');
  const [busy, setBusy] = useState(false);
  const [freeform, setFreeform] = useState(false);
  const [freeText, setFreeText] = useState('');
  // Optional real client to attach (persona alone does not link a buyer).
  const [clientId, setClientId] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);

  const searchClients = async (q) => {
    setClientQuery(q);
    if (!q || q.length < 2) { setClientResults([]); return; }
    try { const res = await searchTrainingClients(q); setClientResults(res?.clients || []); }
    catch { setClientResults([]); }
  };

  const [promptSets, setPromptSets] = useState(null); // { persona: {label, steps} }

  useEffect(() => {
    let live = true;
    getIntakePrompts()
      .then((res) => {
        if (!live || !res?.personas) return;
        const map = {};
        res.personas.forEach((p) => { map[p.persona] = { label: p.label, steps: p.steps }; });
        setPromptSets(map);
      })
      .catch(() => { /* fall back to bundled STEPS */ });
    return () => { live = false; };
  }, []);

  const personaTabs = promptSets
    ? PERSONAS.map((p) => ({ id: p.id, label: promptSets[p.id]?.label || p.label }))
    : PERSONAS;
  const steps = (promptSets && promptSets[persona]?.steps) || STEPS[persona];
  // If prompts load/shrink (or persona switches) below the current index, clamp so
  // we never index past the end (which would crash on cur.key).
  useEffect(() => {
    if (step > steps.length) setStep(steps.length);
  }, [steps.length, persona]);
  // Last "step" is the buyer-stage selector.
  const onBuyerStep = step >= steps.length;
  const cur = steps[Math.min(step, steps.length - 1)];

  const setAns = (v) => setAnswers((a) => ({ ...a, [cur.key]: v }));

  const submit = async (payloadOverrides = {}) => {
    setBusy(true);
    try {
      const { request } = await createRequest({
        request_source: persona,
        training_need: answers.need,
        program_type: answers.program_type,
        audience: answers.audience,
        learner_count: parseInt(answers.learner_count, 10) || null,
        urgency: answers.urgency,
        buyer_stage: buyerStage,
        client_id: clientId || null,
        intake_answers: answers,
        ...payloadOverrides,
      });
      const { concept } = await generateConcept(request.id);
      toast.success('Concept generated');
      onGenerated?.(concept.id);
    } catch (e) {
      toast.error(e?.message || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step < steps.length) {
      setStep(step + 1);
    } else {
      submit();
    }
  };

  if (freeform) {
    return (
      <div className="max-w-3xl mx-auto rounded-2xl border border-blue-100 dark:border-gray-700 bg-gradient-to-b from-blue-50/60 to-white dark:from-gray-800/40 dark:to-gray-900 p-6">
        <button onClick={() => setFreeform(false)} className="text-xs text-blue-600 mb-3">← Back to guided steps</button>
        <h2 className="text-2xl font-bold mb-1">Describe everything at once</h2>
        <p className="text-sm text-gray-500 mb-4">Tell us the whole situation - JPRO will shape it into a concept and back-fill the details.</p>
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          className="w-full min-h-[160px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-sm"
          placeholder="e.g. Our LIHTC site staff in Ohio keep miscalculating asset income on annual recerts, it's showing up in file reviews, and we have ~120 people who need this before Q3…"
        />
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-widest text-blue-600 font-bold mb-2">How ready is this?</div>
          <div className="flex flex-wrap gap-2">
            {BUYER_STAGES.map(([v, l]) => (
              <button
                key={v}
                onClick={() => setBuyerStage(v)}
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  buyerStage === v ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-500'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            disabled={busy || !freeText.trim()}
            onClick={() => submit({ training_need: freeText, intake_answers: { freeform: freeText } })}
            className="text-sm font-semibold px-5 py-2.5 rounded-xl bg-orange-500 text-white disabled:opacity-50"
          >
            {busy ? 'Generating…' : 'Generate concept'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto rounded-2xl border border-blue-100 dark:border-gray-700 bg-gradient-to-b from-blue-50/60 to-white dark:from-gray-800/40 dark:to-gray-900 p-6">
      <div className="flex gap-2 mb-6">
        {personaTabs.map((p) => (
          <button
            key={p.id}
            onClick={() => {
              setPersona(p.id);
              setStep(0);
            }}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              persona === p.id ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-500'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex gap-1.5 mb-5">
        {Array.from({ length: steps.length + 1 }).map((_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded ${i <= step ? 'bg-blue-600' : 'bg-blue-100 dark:bg-gray-700'}`} />
        ))}
      </div>

      {onBuyerStep ? (
        <>
          <div className="text-[11px] uppercase tracking-widest text-blue-600 font-bold">Final step</div>
          <h2 className="text-2xl font-bold mt-1 mb-3">How ready is this to move forward?</h2>
          <div className="flex flex-wrap gap-2">
            {BUYER_STAGES.map(([v, l]) => (
              <button
                key={v}
                onClick={() => setBuyerStage(v)}
                className={`text-sm px-4 py-2 rounded-lg border ${
                  buyerStage === v ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-500'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Attach a client (optional)</div>
            {clientId ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-600">{clientName}</span>
                <button onClick={() => { setClientId(null); setClientName(''); setClientQuery(''); setClientResults([]); }} className="text-xs text-gray-500">clear</button>
              </div>
            ) : (
              <div className="relative max-w-sm">
                <input
                  value={clientQuery}
                  onChange={(e) => searchClients(e.target.value)}
                  placeholder="Search clients by company name…"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                {clientResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow max-h-48 overflow-auto">
                    {clientResults.map((cl) => (
                      <button key={cl.id} onClick={() => { setClientId(cl.id); setClientName(cl.name); setClientResults([]); }}
                        className="block w-full text-left text-sm px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700">{cl.name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-1">The persona is just a voice; attach a client here to link a real buyer.</p>
          </div>
        </>
      ) : (
        <>
          <div className="text-[11px] uppercase tracking-widest text-blue-600 font-bold">
            Step {step + 1} of {steps.length}
          </div>
          <h2 className="text-2xl font-bold mt-1 mb-1">{cur.q}</h2>
          {cur.help && <p className="text-sm text-gray-500 mb-4">{cur.help}</p>}
          <textarea
            value={answers[cur.key] || ''}
            onChange={(e) => setAns(e.target.value)}
            className="w-full min-h-[90px] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-sm"
          />
          <div className="mt-3 text-xs text-gray-500">
            In a hurry?{' '}
            <button onClick={() => setFreeform(true)} className="text-blue-600 font-semibold">
              Describe everything at once →
            </button>
          </div>
        </>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={() => (step === 0 ? onCancel?.() : setStep(step - 1))} className="text-sm text-gray-500">
          {step === 0 ? '← All concepts' : '← Back'}
        </button>
        <button disabled={busy} onClick={next} className="text-sm font-semibold px-5 py-2.5 rounded-xl bg-orange-500 text-white disabled:opacity-50">
          {busy ? 'Generating…' : onBuyerStep ? 'Generate concept' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
